import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError, errorMessages } from '../../api/client'
import { moduleAlarmsQuery } from '../../api/settingsModuleAlarms'
import {
  addListItems,
  changeListItemStatus,
  changeRegKeyMode,
  regKeyProblem,
  type RegKeyMode,
  deleteListItem,
  isSecuriteeTab,
  LIST_OF_TAB,
  securiteeListQuery,
  securiteeShellQuery,
  type ListItem,
  type ListKind,
  type SecuriteeTab,
} from '../../api/settingsSecuritee'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { ModuleAlarmsEditor } from '../moduleAlarms/ModuleAlarmsEditor'
import { LiveAlarmsEditor } from '../proactivitee/LiveAlarmsEditor'
import { Microsoft365Panel } from './Microsoft365Panel'
import { ControlledFolderAccessPanel } from './ControlledFolderAccessPanel'
import { ExtensionFeedPanel } from './ExtensionFeedPanel'
import { AbuseIpdbPanel } from './AbuseIpdbPanel'
import { GravityZonePanel } from './GravityZonePanel'
import { XdrPanel } from './XdrPanel'

/**
 * Settings → Securitee: `securitee_settings/tabList.php` + fiecare ecran,
 * element cu element. Antivirus / Antivirus Exclusion Groups nu sunt aici:
 * configurau prototipul Bitdefender, lasat.
 */
export function SecuriteeSettingsPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(securiteeShellQuery())

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isSecuriteeTab(tab)) return
    const first = shell.data.tabs[0]
    if (first) void navigate({ to: '/settings/securitee/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const current = isSecuriteeTab(tab) ? tab : (shell.data.tabs[0]?.id ?? '')

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Securitee</li>
          {shell.data.tabs.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'cryptolocker_protection' && <ProtectionList kind={LIST_OF_TAB.cryptolocker_protection} />}
        {current === 'registry_immunizer' && <ProtectionList kind={LIST_OF_TAB.registry_immunizer} />}
        {current === 'files_creation_blocker' && <ProtectionList kind={LIST_OF_TAB.files_creation_blocker} />}
        {current === 'controlled_folder_access' && <ControlledFolderAccessPanel />}
        {current === 'alarms' && <AlarmsPanel />}
        {current === 'microsoft365' && <Microsoft365Panel />}
        {current === 'gravityzone' && <GravityZonePanel />}
        {current === 'abuseipdb' && <AbuseIpdbPanel />}
        {current === 'xdr' && <XdrPanel />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: SecuriteeTab; current: string }) {
  const navigate = useNavigate()
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    void navigate({ to: '/settings/securitee/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={tab.id === current ? 'menu-tab active' : 'menu-tab'}>
      <a title={tab.title} href={'/app/settings/securitee/' + tab.id} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/** Textele celor trei ecrane (cryptolocker/index.php, registry_immunizer/index.php, files_creation_blocker/index.php). */
const COPY: Record<ListKind, { strong: string; rest: string; sub: string; add: string; modalTitle: string; modalId: string; formId: string }> = {
  extensions: { strong: 'CryptoLocker', rest: 'protection', sub: 'cryptolocker protection settings', add: 'Add extensions', modalTitle: 'Add extensions', modalId: 'addExtensionsModal', formId: 'addExtensions' },
  reg_keys: { strong: 'Registry', rest: 'immunizer', sub: 'registry immunizer settings', add: 'Add reg keys', modalTitle: 'Add registry keys', modalId: 'addRegKeysModal', formId: 'addRegKeys' },
  files: { strong: 'Files creation', rest: 'blocker', sub: 'files creation blocker settings', add: 'Add files', modalTitle: 'Add files', modalId: 'addBlockerFilesModal', formId: 'addBlockerFiles' },
}

function itemName(item: ListItem): string {
  return item.extension ?? item.key_name ?? item.file_name ?? ''
}

function ProtectionList({ kind }: { kind: ListKind }) {
  const client = useQueryClient()
  const page = useQuery(securiteeListQuery(kind))
  const [open, setOpen] = useState(false)
  const copy = COPY[kind]
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'securitee', 'lists', kind] })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const canWrite = page.data.canWrite

  return (
    <div id="content">
      <header className="panel-heading">
        <h3>
          <strong>{copy.strong}</strong> {copy.rest}
        </h3>
        <label className="color">{copy.sub}</label>
        {canWrite && (
          <button className="pull-right btn btn-primary btn-sm" onClick={() => setOpen(true)}>
            <i className="fas fa-plus-circle"></i> {copy.add}
          </button>
        )}
      </header>
      {kind === 'extensions' && (
        <div className="alert alert-danger">
          Ensure that the <strong>enabled extension</strong> match with a <strong>cryptolocker extension</strong>, otherwise OptimumDesk will lockdown any device with that activated extension.
        </div>
      )}
      {kind === 'reg_keys' && (
        <div className="alert alert-info securitee-reg-keys-help">
          On devices with Registry Immunizer set to Block, the agent watches the values of these keys and reports every change in the device&apos;s Events. Keys set to <strong>Revert</strong> are also put back. Only <strong>HKLM</strong> keys can be watched. Suggested: <code>HKLM\SOFTWARE\Microsoft\Windows\CurrentVersion\Run</code>, <code>...\RunOnce</code> and the same two under <code>HKLM\SOFTWARE\WOW6432Node\Microsoft\Windows\CurrentVersion</code>.
        </div>
      )}
      <ul className="list-group">
        {page.data.items.map((item) => (
          <li className="list-group-item" key={item.ID}>
            <div style={{ display: 'inline-block', width: 500 }}>{itemName(item)}</div>
            {kind === 'reg_keys' && (
              <div className="pull-right" style={{ marginLeft: 20 }}>
                {canWrite ? (
                  <select
                    className="form-control input-sm reg-key-mode"
                    aria-label={`${itemName(item)}: when it changes`}
                    value={item.mode ?? 'report'}
                    onChange={(e) => {
                      void changeRegKeyMode(item.ID, e.target.value as RegKeyMode)
                        .then(reload)
                        .catch((err: unknown) => toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error'))
                    }}
                  >
                    <option value="report">Report</option>
                    <option value="revert">Revert</option>
                  </select>
                ) : (
                  <span className="label label-default">{item.mode === 'revert' ? 'Revert' : 'Report'}</span>
                )}
              </div>
            )}
            {canWrite ? (
              <>
                <div className="pull-right" style={{ marginLeft: 20 }}>
                  <button
                    className="btn btn-danger btn-xs deleteExtension"
                    onClick={() => {
                      if (!confirm('Are you sure?')) return
                      void deleteListItem(kind, item.ID).then(reload)
                    }}
                  >
                    Delete
                  </button>
                </div>
                <div className="pull-right">
                  <label className={item.status === 1 ? 'icheckbox_line-green checked' : 'icheckbox_line-red'} style={{ cursor: 'pointer' }}>
                    <input
                      className="check"
                      type="checkbox"
                      checked={item.status === 1}
                      onChange={(e) => {
                        void changeListItemStatus(kind, item.ID, e.target.checked ? 1 : 0).then(reload)
                      }}
                    />
                    <div className="icheck_line-icon"></div> Enabled
                  </label>
                </div>
              </>
            ) : (
              <div className="pull-right">
                <span className={item.status === 1 ? 'label label-success' : 'label label-danger'}>{item.status === 1 ? 'Enabled' : 'Disabled'}</span>
              </div>
            )}
          </li>
        ))}
      </ul>
      {kind === 'extensions' && <ExtensionFeedPanel />}
      {canWrite && <AddItemsModal kind={kind} open={open} onClose={() => setOpen(false)} onSaved={reload} />}
    </div>
  )
}

/** `cryptolocker/others/addExtensionsModal.php` si surorile: un rand de text, plus/minus, Save. */
function AddItemsModal({ kind, open, onClose, onSaved }: { kind: ListKind; open: boolean; onClose: () => void; onSaved: () => void }) {
  const copy = COPY[kind]
  const [values, setValues] = useState<string[]>([''])
  const [errors, setErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState<RegKeyMode>('report')
  const close = () => {
    setValues([''])
    setErrors([])
    setMode('report')
    onClose()
  }
  const save = async (e?: FormEvent) => {
    e?.preventDefault()
    if (busy) return
    const clean = values.map((v) => v.trim()).filter((v) => v !== '')
    if (kind === 'reg_keys') {
      /* Cheile de registru pot avea spatii (`HKLM\SOFTWARE\My App`); regula lor e alta. */
      const problems = [...new Set(clean.map(regKeyProblem).filter((p) => p !== ''))]
      if (problems.length > 0) {
        setErrors(problems)
        return
      }
    } else if (clean.some((v) => v.includes(' '))) {
      setErrors([kind === 'extensions' ? 'Extension must not contain white spaces' : 'Value must not contain white spaces'])
      return
    }
    if (clean.length === 0) {
      setErrors(['This field is required.'])
      return
    }
    setBusy(true)
    try {
      await addListItems(kind, clean, kind === 'reg_keys' ? mode : undefined)
      toast('Saved', 'Success', 'success')
      onSaved()
      close()
    } catch (err) {
      setErrors(errorMessages(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      id={copy.modalId}
      open={open}
      onClose={close}
      title={copy.modalTitle}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={close}>
            Close
          </button>
          <button type="button" className="btn btn-primary saveExtensions" disabled={busy} onClick={() => void save()}>
            Save
          </button>
        </>
      }
    >
      <form className="form-horizontal labelcustomize" id={copy.formId} method="post" onSubmit={(e) => void save(e)}>
        {kind === 'extensions' && (
          <div className="alert alert-danger">
            <strong>DO NOT</strong> add other extensions than <strong>cryptolocker extensions</strong>. If any of this extensions will be found, OptimumDesk will lockdown your device.
            <br />
            You are adding this extensions on your own risk.
          </div>
        )}
        {values.map((value, i) => (
          <div className="form-group" key={i}>
            <div className="col-sm-10 inputGroupContainer">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  name={kind + '[]'}
                  value={value}
                  required
                  onChange={(e) => setValues(values.map((x, j) => (j === i ? e.target.value : x)))}
                />
                <span className="input-group-btn">
                  <button className="btn btn-default btn-success add-extension" type="button" onClick={() => setValues([...values.slice(0, i + 1), '', ...values.slice(i + 1)])}>
                    <i className="fas fa-plus"></i>
                  </button>
                  <button
                    className="btn btn-default btn-danger remove-extension"
                    type="button"
                    onClick={() => setValues(values.length === 1 ? [''] : values.filter((_, j) => j !== i))}
                  >
                    <i className="fas fa-minus"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
        ))}
        {kind === 'reg_keys' && (
          <div className="form-group">
            <label className="col-sm-4 control-label" htmlFor="reg-keys-mode">
              When a key changes
            </label>
            <div className="col-sm-6">
              <select id="reg-keys-mode" name="mode" className="form-control" value={mode} onChange={(e) => setMode(e.target.value as RegKeyMode)}>
                <option value="report">Report the change</option>
                <option value="revert">Report and put the value back</option>
              </select>
            </div>
          </div>
        )}
        {errors.map((message) => (
          <div className="alert alert-danger" key={message}>
            {message}
          </div>
        ))}
      </form>
    </Modal>
  )
}

/** `securitee_settings/alarms/index.php`: Protection (alarmele proprii) si Workstation security (usa Securitee). */
function AlarmsPanel() {
  const page = useQuery(moduleAlarmsQuery('/settings/securitee'))
  const [pane, setPane] = useState<'protection' | 'workstation'>('protection')
  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  return (
    <>
      <h3 className="ad-title">Alarms</h3>
      <p className="ad-blurb">
        Protection is cryptolocker, registry and file blocker. Workstation security is antivirus, firewall and Windows Update — the same engine as System Alarms, edited here.
      </p>
      <div className="ad-layers">
        <button type="button" className={pane === 'protection' ? 'sec-alarms-pane is-on' : 'sec-alarms-pane'} data-pane="protection" onClick={() => setPane('protection')}>
          Protection
        </button>
        {page.data.workstation && (
          <button type="button" className={pane === 'workstation' ? 'sec-alarms-pane is-on' : 'sec-alarms-pane'} data-pane="workstation" onClick={() => setPane('workstation')}>
            Workstation security
          </button>
        )}
      </div>
      <div id="sec-alarms-body">
        {pane === 'protection' ? <ModuleAlarmsEditor base="/settings/securitee" categories={page.data.categories} /> : <LiveAlarmsEditor door="securitee" />}
      </div>
    </>
  )
}
